import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Loader2, User, LogIn, PackageCheck, Package, Clock, CheckCircle2, ArrowLeft, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_75f76eb1-ed0f-4a2e-8af9-a573c953ed2b/artifacts/jcb4nruu_7d3b96f2-f08e-453b-8c2c-591199068bb4.jpeg";

const STATUS_LABELS = {
  pending: { label: "Pendente", color: "bg-slate-200 text-slate-800" },
  in_progress: { label: "Em produção", color: "bg-blue-100 text-blue-800" },
  paused: { label: "Suspensa", color: "bg-amber-100 text-amber-800" },
  completed: { label: "Concluída", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelada", color: "bg-red-100 text-red-800" },
};

function getToken() { return localStorage.getItem("portal_token"); }
function setToken(t) { if (t) localStorage.setItem("portal_token", t); else localStorage.removeItem("portal_token"); }

/* --------- LOGIN PAGE --------- */
export function PortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (getToken()) navigate("/portal/dashboard", { replace: true }); }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/portal/login`, { email, password });
      setToken(r.data.token);
      toast.success(`Bem-vindo ${r.data.client_name || ""}`);
      navigate("/portal/dashboard", { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200">
      <Card className="w-full max-w-md shadow-xl" data-testid="portal-login">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="EP3D" className="h-20 mx-auto mb-2" />
          <CardTitle>Portal do Cliente</CardTitle>
          <CardDescription>Consulte encomendas, faturas e certificados</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="portal-email" required />
            </div>
            <div><Label>Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="portal-password" required />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="portal-login-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <LogIn className="h-4 w-4 mr-1" />}
              Entrar
            </Button>
            <p className="text-xs text-center text-slate-500 mt-4">
              Sem conta? Solicite convite ao seu contacto EP3D.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/* --------- REGISTER (from invite link) --------- */
export function PortalRegister() {
  const navigate = useNavigate();
  const loc = useLocation();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== password2) { toast.error("Passwords não coincidem"); return; }
    if (password.length < 6) { toast.error("Password muito curta"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/portal/register`, { invite_token: token, password });
      setToken(r.data.token);
      toast.success("Conta criada!");
      navigate("/portal/dashboard", { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="EP3D" className="h-20 mx-auto mb-2" />
          <CardTitle>Ativar conta</CardTitle>
          <CardDescription>Define a tua password para aceder ao portal</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Nova password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-testid="portal-newpw" />
            </div>
            <div><Label>Confirmar</Label>
              <Input type="password" value={password2} onChange={e => setPassword2(e.target.value)} required minLength={6} data-testid="portal-newpw2" />
            </div>
            <Button type="submit" className="w-full" disabled={loading || !token} data-testid="portal-register-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Ativar
            </Button>
            {!token && <p className="text-xs text-red-600 text-center">Link de convite inválido.</p>}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/* --------- LAYOUT WRAPPER --------- */
function PortalShell({ children }) {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => {
    const t = getToken();
    if (!t) { navigate("/portal/login", { replace: true }); return; }
    axios.get(`${API}/portal/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setMe(r.data))
      .catch(() => { setToken(null); navigate("/portal/login", { replace: true }); });
  }, [navigate]);

  const logout = () => { setToken(null); navigate("/portal/login", { replace: true }); };
  if (!me) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="EP3D" className="h-10" />
            <div>
              <div className="font-bold">Portal do Cliente</div>
              <div className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" />{me.client_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-right">
              <div className="font-semibold">{me.contact_name || me.email}</div>
              <div className="text-xs text-slate-500">{me.email}</div>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="portal-logout">Sair</Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto p-4 space-y-4">{children}</main>
    </div>
  );
}

/* --------- DASHBOARD --------- */
export function PortalDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = getToken();
    if (!t) return;
    axios.get(`${API}/portal/dashboard`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setData(r.data))
      .catch(() => toast.error("Erro"));
  }, []);
  if (!data) return <PortalShell><div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div></PortalShell>;

  return (
    <PortalShell>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total encomendas" value={data.total_orders} icon={Package} color="#0b1f3a" />
        <StatCard label="Em produção" value={data.active_orders} icon={Clock} color="#f97316" />
        <StatCard label="Concluídas" value={data.completed_orders} icon={CheckCircle2} color="#16a34a" />
        <StatCard label="Empresa" value={data.client?.name || "-"} icon={Building2} isText />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Últimas encomendas</CardTitle>
          <Button size="sm" onClick={() => navigate("/portal/orders")} data-testid="see-all-orders">Ver todas</Button>
        </CardHeader>
        <CardContent>
          {data.recent.length === 0 ? (
            <div className="text-center py-6 text-slate-500">Ainda sem encomendas registadas.</div>
          ) : (
            <div className="space-y-2">
              {data.recent.map(o => (
                <button key={o.order_number} onClick={() => navigate(`/portal/orders/${o.order_number}`)} className="w-full text-left p-3 border rounded-lg hover:bg-slate-50 flex justify-between items-center" data-testid={`portal-order-${o.order_number}`}>
                  <div>
                    <div className="font-bold">{o.order_number} · {o.part_code}</div>
                    <div className="text-xs text-slate-500">{o.part_description} · Qty: {o.quantity_ordered}</div>
                  </div>
                  <StatusPill s={o.status} />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PortalShell>
  );
}

function StatCard({ label, value, icon: Icon, color, isText }) {
  return (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-1 text-xs text-slate-500 uppercase"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`${isText ? "text-lg" : "text-3xl"} font-black mt-1`} style={{ color }}>{value ?? "-"}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const m = STATUS_LABELS[s] || { label: s, color: "bg-slate-100" };
  return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${m.color}`}>{m.label}</span>;
}

/* --------- ORDERS LIST --------- */
export function PortalOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = getToken(); if (!t) return;
    axios.get(`${API}/portal/orders`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setOrders(r.data)).catch(() => toast.error("Erro"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalShell>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
      <Card>
        <CardHeader><CardTitle>Minhas encomendas ({orders.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-6" /> :
           orders.length === 0 ? <div className="text-center py-8 text-slate-500">Ainda sem encomendas.</div> :
           <div className="overflow-x-auto">
             <table className="w-full text-sm">
               <thead className="bg-slate-50">
                 <tr>
                   <th className="text-left p-2">Nº</th>
                   <th className="text-left p-2">Peça</th>
                   <th className="text-right p-2">Qty</th>
                   <th className="text-right p-2">Produzidas</th>
                   <th className="text-left p-2">Entrega</th>
                   <th className="text-left p-2">Estado</th>
                 </tr>
               </thead>
               <tbody>
                 {orders.map(o => (
                   <tr key={o.order_number} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/portal/orders/${o.order_number}`)}>
                     <td className="p-2 font-mono">{o.order_number}</td>
                     <td className="p-2">{o.part_code} <span className="text-xs text-slate-500">{o.part_description}</span></td>
                     <td className="p-2 text-right">{o.quantity_ordered}</td>
                     <td className="p-2 text-right">
                       <span className="font-semibold">{o.quantity_produced || 0}</span>
                       <span className="text-xs text-slate-500"> / {o.quantity_ordered}</span>
                     </td>
                     <td className="p-2 text-xs">{o.due_date ? new Date(o.due_date).toLocaleDateString("pt-PT") : "-"}</td>
                     <td className="p-2"><StatusPill s={o.status} /></td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>}
        </CardContent>
      </Card>
    </PortalShell>
  );
}

/* --------- ORDER DETAIL --------- */
export function PortalOrderDetail() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = getToken(); if (!t) return;
    axios.get(`${API}/portal/orders/${orderNumber}`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setOrder(r.data)).catch(() => toast.error("Erro"))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  const pct = order ? Math.min(100, Math.round(((order.quantity_produced || 0) / (order.quantity_ordered || 1)) * 100)) : 0;

  return (
    <PortalShell>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
      {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-6" /> :
       !order ? <div className="p-6 text-slate-500">Não encontrada</div> :
      <>
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>{order.order_number}</CardTitle>
                <CardDescription>{order.part_code} — {order.part_description}</CardDescription>
              </div>
              <StatusPill s={order.status} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Progresso</span>
                <b>{order.quantity_produced || 0} / {order.quantity_ordered} peças ({pct}%)</b>
              </div>
              <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full ${order.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <Info label="Quantidade encomendada" value={order.quantity_ordered} />
              <Info label="Quantidade produzida" value={order.quantity_produced || 0} />
              <Info label="Data de criação" value={order.created_at ? new Date(order.created_at).toLocaleString("pt-PT") : "-"} />
              <Info label="Data de entrega" value={order.due_date ? new Date(order.due_date).toLocaleString("pt-PT") : "-"} />
              {order.completed_at && <Info label="Concluída em" value={new Date(order.completed_at).toLocaleString("pt-PT")} />}
              {order.notes && <Info label="Notas" value={order.notes} />}
            </div>
          </CardContent>
        </Card>
      </>}
    </PortalShell>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className="font-semibold">{value ?? "-"}</div>
    </div>
  );
}
